import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, LineChart, PieChart, TrendingUp, DollarSign, Package, CalendarDays, Download, Eye, Users, Folder, ShoppingBag } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { generatePdfReport } from '../../utils/pdfGenerator'; // Importar el generador de PDF
import { products as allProducts, categories as allCategories } from '../../data/mockData'; // Importar todos los productos y categorías

const mockSalesData = [
  { month: 'Ene', sales: 12000, orders: 150, products: 500 },
  { month: 'Feb', sales: 15000, orders: 180, products: 600 },
  { month: 'Mar', sales: 13000, orders: 160, products: 550 },
  { month: 'Abr', sales: 17000, orders: 200, products: 700 },
  { month: 'May', sales: 19000, orders: 220, products: 750 },
  { month: 'Jun', sales: 22000, orders: 250, products: 800 },
  { month: 'Jul', sales: 20000, orders: 230, products: 720 },
  { month: 'Ago', sales: 24000, orders: 270, products: 850 },
  { month: 'Sep', sales: 21000, orders: 240, products: 780 },
  { month: 'Oct', sales: 26000, orders: 290, products: 900 },
  { month: 'Nov', sales: 28000, orders: 310, products: 950 },
  { month: 'Dic', sales: 30000, orders: 330, products: 1000 },
];

const mockProductVisits = [
  { name: 'Hoteles Patagonia 2025 - 2026', visits: 1250, sales: 150 },
  { name: 'Hoteles Sur 2025 - 2026', visits: 980, sales: 75 },
  { name: 'Hoteles Santiago 2025 - 2026', visits: 720, sales: 200 },
  { name: 'Aspiradora Robot', visits: 600, sales: 90 },
  { name: 'Bicicleta Montaña Pro', visits: 550, sales: 45 },
  { name: 'Reloj Inteligente Sport', visits: 480, sales: 110 },
];

const mockCategoryVisits = [
  { name: 'Hoteleria Chile', visits: 3500, productsViewed: 1500 },
  { name: 'Servicios Turísticos', visits: 1800, productsViewed: 700 },
  { name: 'All Inclusive', visits: 1200, productsViewed: 500 },
  { name: 'Turismo de Nieve', visits: 900, productsViewed: 300 },
];

const mockAgencyAccess = [
  { name: 'Agencia Viajes Pro', accesses: 520, lastLogin: '2024-07-20' },
  { name: 'Operador Elite', accesses: 410, lastLogin: '2024-07-19' },
  { name: 'Agencia Aventuras XYZ', accesses: 300, lastLogin: '2024-07-18' },
  { name: 'Turismo Global Solutions', accesses: 250, lastLogin: '2024-07-17' },
];

// Datos de evolución de productos (simulados)
const mockProductEvolution = [
  { month: 'Ene', newProducts: 5, activeProducts: 20 },
  { month: 'Feb', newProducts: 3, activeProducts: 22 },
  { month: 'Mar', newProducts: 7, activeProducts: 28 },
  { month: 'Abr', newProducts: 4, activeProducts: 30 },
  { month: 'May', newProducts: 6, activeProducts: 35 },
  { month: 'Jun', newProducts: 2, activeProducts: 36 },
  { month: 'Jul', newProducts: 8, activeProducts: 42 },
];


const SalesStatisticsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('yearly'); // monthly, quarterly, yearly

  if (!isAdmin) {
    navigate('/admin'); // Redirigir si no es administrador
    return null;
  }

  // Nuevos KPIs
  const totalProducts = allProducts.length;
  const totalCategories = allCategories.length;
  const totalUsers = 4; // Simulando el número de usuarios del mock

  const kpiCards = [
  { title: 'Servicios/Hoteles', value: totalProducts.toLocaleString(), icon: Package, color: 'text-green-600', bg: 'bg-green-100' },
  { title: 'Categorías Turísticas', value: totalCategories.toLocaleString(), icon: Folder, color: 'text-blue-600', bg: 'bg-blue-100' },
  { title: 'Usuarios/Agencias', value: totalUsers.toLocaleString(), icon: Users, color: 'text-purple-600', bg: 'bg-purple-100' },
  ];

  // Funciones para exportar a PDF
  const exportProductVisitsPdf = () => {
    const headers = ['Producto', 'Visitas', 'Ventas'];
    const data = mockProductVisits.map(p => [p.name, p.visits, p.sales]);
    generatePdfReport('Reporte de Productos Más Visitados', headers, data, 'productos_mas_visitados');
  };

  const exportCategoryVisitsPdf = () => {
    const headers = ['Categoría', 'Visitas', 'Productos Vistos'];
    const data = mockCategoryVisits.map(c => [c.name, c.visits, c.productsViewed]);
    generatePdfReport('Reporte de Categorías Más Visitadas', headers, data, 'categorias_mas_visitadas');
  };

  const exportAgencyAccessPdf = () => {
    const headers = ['Agencia', 'Accesos', 'Último Acceso'];
    const data = mockAgencyAccess.map(a => [a.name, a.accesses, a.lastLogin]);
    generatePdfReport('Reporte de Accesos de Agencias', headers, data, 'accesos_agencias');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Estadísticas de Turismo y Hotelería</h2>
          <p className="text-xl text-gray-600">Análisis de reservas, servicios y actividad turística.</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {kpiCards.map((card, index) => (
            <motion.div
              key={index}
              className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20 flex items-center gap-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${card.bg}`}>
                <card.icon className={`w-7 h-7 ${card.color}`} />
              </div>
              <div>
                <p className="text-gray-500 text-sm font-medium">{card.title}</p>
                <h3 className="text-3xl font-bold text-gray-900">{card.value}</h3>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Evolución de Servicios y Hoteles</h3>
            <div className="flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setTimeframe('monthly')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${timeframe === 'monthly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Mensual
              </button>
              <button
                onClick={() => setTimeframe('quarterly')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${timeframe === 'quarterly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Trimestral
              </button>
              <button
                onClick={() => setTimeframe('yearly')}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${timeframe === 'yearly' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
              >
                Anual
              </button>
            </div>
          </div>
          
          {/* Placeholder for Product Evolution Chart */}
          <div className="h-80 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200 text-gray-500">
            <LineChart className="w-16 h-16 text-gray-300 mr-4" />
            <p className="text-lg">Gráfico de evolución de productos aquí (requiere biblioteca de gráficos)</p>
          </div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {/* Reporte de Productos Más Visitados */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Servicios/Hoteles Más Visitados</h3>
              <motion.button
                onClick={exportProductVisitsPdf}
                className="p-2 bg-gray-100 rounded-full text-gray-600 hover:bg-gray-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Download className="w-5 h-5" />
              </motion.button>
            </div>
            <ul className="space-y-3">
              {mockProductVisits.map((product, index) => (
                <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-blue-500" />
                    <span className="font-medium text-gray-800">{product.name}</span>
                  </div>
                  <span className="text-gray-600">{product.visits} visitas</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Reporte de Categorías Más Visitadas */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Categorías Turísticas Más Visitadas</h3>
              <motion.button
                onClick={exportCategoryVisitsPdf}
                className="p-2 bg-gray-100 rounded-full text-gray-600 hover:bg-gray-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Download className="w-5 h-5" />
              </motion.button>
            </div>
            <ul className="space-y-3">
              {mockCategoryVisits.map((category, index) => (
                <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Folder className="w-4 h-4 text-purple-500" />
                    <span className="font-medium text-gray-800">{category.name}</span>
                  </div>
                  <span className="text-gray-600">{category.visits} visitas</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Reporte de Usuarios/Agencias con Más Acceso */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-white/20 md:col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Agencias/Operadores con Más Acceso</h3>
              <motion.button
                onClick={exportAgencyAccessPdf}
                className="p-2 bg-gray-100 rounded-full text-gray-600 hover:bg-gray-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Download className="w-5 h-5" />
              </motion.button>
            </div>
            <ul className="space-y-3">
              {mockAgencyAccess.map((agency, index) => (
                <li key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-500" />
                    <span className="font-medium text-gray-800">{agency.name}</span>
                  </div>
                  <span className="text-gray-600">{agency.accesses} accesos (Último: {agency.lastLogin})</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SalesStatisticsPage;